import { useEffect, useRef } from "react";

interface VoiceOrbProps {
  isActive: boolean;
  isSpeaking: boolean;
}

const VoiceOrb = ({ isActive, isSpeaking }: VoiceOrbProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationId: number;
    let rotation = 0;

    const drawOrb = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const baseRadius = isSpeaking ? 100 : 80;
      
      // Enhanced outer glow with multiple colors
      const outerGlow = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, baseRadius + 60);
      outerGlow.addColorStop(0, 'rgba(16, 185, 129, 0.4)');
      outerGlow.addColorStop(0.3, 'rgba(6, 182, 212, 0.3)');
      outerGlow.addColorStop(0.6, 'rgba(59, 130, 246, 0.2)');
      outerGlow.addColorStop(1, 'rgba(16, 185, 129, 0)');
      
      ctx.fillStyle = outerGlow;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Enhanced animated rings with color variation
      for (let i = 0; i < 4; i++) {
        ctx.save();
        ctx.translate(centerX, centerY);
        ctx.rotate((rotation + i * 90) * Math.PI / 180);
        
        // Color shifts between teal, cyan, and blue
        const colors = [
          `rgba(16, 185, 129, ${0.4 - i * 0.08})`,   // primary
          `rgba(6, 182, 212, ${0.35 - i * 0.08})`,   // cyan
          `rgba(59, 130, 246, ${0.3 - i * 0.08})`,   // blue
          `rgba(147, 51, 234, ${0.25 - i * 0.08})`   // purple
        ];
        ctx.strokeStyle = colors[i];
        ctx.lineWidth = isSpeaking ? 3 : 2;
        ctx.beginPath();
        
        const ringRadius = baseRadius + (isSpeaking ? Math.sin(Date.now() / 150 + i) * 15 : 5);
        
        for (let angle = 0; angle < 360; angle += 8) {
          const rad = (angle * Math.PI) / 180;
          const wave = Math.sin(angle / 15 + rotation / 15) * (isSpeaking ? 8 : 3);
          const r = ringRadius + wave;
          const x = Math.cos(rad) * r;
          const y = Math.sin(rad) * r;
          
          if (angle === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        }
        
        ctx.closePath();
        ctx.stroke();
        ctx.restore();
      }

      // Enhanced center orb with multi-color gradient
      const centerGradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, baseRadius * 0.85);
      centerGradient.addColorStop(0, 'rgba(255, 255, 255, 0.9)');
      centerGradient.addColorStop(0.2, 'rgba(16, 185, 129, 0.9)');
      centerGradient.addColorStop(0.5, 'rgba(6, 182, 212, 0.7)');
      centerGradient.addColorStop(0.8, 'rgba(59, 130, 246, 0.5)');
      centerGradient.addColorStop(1, 'rgba(16, 185, 129, 0.2)');
      
      ctx.fillStyle = centerGradient;
      ctx.beginPath();
      ctx.arc(centerX, centerY, baseRadius * 0.85, 0, Math.PI * 2);
      ctx.fill();
      
      // Add inner highlight
      const highlightGradient = ctx.createRadialGradient(
        centerX - 20, centerY - 20, 0,
        centerX, centerY, baseRadius * 0.5
      );
      highlightGradient.addColorStop(0, 'rgba(255, 255, 255, 0.6)');
      highlightGradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
      
      ctx.fillStyle = highlightGradient;
      ctx.beginPath();
      ctx.arc(centerX, centerY, baseRadius * 0.85, 0, Math.PI * 2);
      ctx.fill();

      // Enhanced magical particles
      if (isActive) {
        const particleCount = isSpeaking ? 30 : 15;
        for (let i = 0; i < particleCount; i++) {
          const angle = (rotation * 2 + i * (360 / particleCount)) * Math.PI / 180;
          const waveOffset = Math.sin(Date.now() / 250 + i) * 20;
          const distance = baseRadius + 25 + waveOffset;
          const x = centerX + Math.cos(angle) * distance;
          const y = centerY + Math.sin(angle) * distance;
          
          // Color cycling particles
          const colorPhase = (i + rotation / 10) % 3;
          let particleColor;
          if (colorPhase < 1) {
            particleColor = `rgba(16, 185, 129, ${0.8 - (distance - baseRadius) / 60})`;
          } else if (colorPhase < 2) {
            particleColor = `rgba(6, 182, 212, ${0.8 - (distance - baseRadius) / 60})`;
          } else {
            particleColor = `rgba(59, 130, 246, ${0.8 - (distance - baseRadius) / 60})`;
          }
          
          ctx.fillStyle = particleColor;
          ctx.beginPath();
          const particleSize = isSpeaking ? 3 : 2;
          ctx.arc(x, y, particleSize, 0, Math.PI * 2);
          ctx.fill();
          
          // Add glow to particles
          ctx.shadowBlur = isSpeaking ? 8 : 4;
          ctx.shadowColor = particleColor;
        }
        ctx.shadowBlur = 0;
      }

      rotation += isActive ? (isSpeaking ? 2 : 0.5) : 0.2;
      animationId = requestAnimationFrame(drawOrb);
    };

    drawOrb();

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [isActive, isSpeaking]);

  return (
    <canvas
      ref={canvasRef}
      width={400}
      height={400}
      className="mx-auto"
    />
  );
};

export default VoiceOrb;